import React, { useState, useEffect, useRef, useCallback } from "react";
import { IDSStepper, IDSStep, IDSButton, IDSInput, IDSAlert, IDSCard, IDSDialog, IDSTooltip } from "@inera/ids-react";
import { saveJustification } from "../../../services/testResultsAPI";
import { useAutosave } from "../../../hooks/useAutosave";
import AutosaveIndicator from "../../common/AutosaveIndicator";
import InfoTooltip from "../../common/InfoTooltip";

import Collapse from "../../Collapse";
import TestStepDetails from "../TestStepDetails";
import { formatDateTimeNoSeconds } from "../../../utils/dateTime";

const formatTimestamp = (ts) => (ts ? formatDateTimeNoSeconds(ts, ts) : null);

const CollapsibleTestSuite = ({ testSuite, tests, expandSignal, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Expand when a focus navigation targets a case within this suite.
  useEffect(() => {
    if (expandSignal) setIsExpanded(true);
  }, [expandSignal]);

  return (
    <>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem 0',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          outline: 'none'
        }}
      >
        <span
          className={isExpanded ? 'ids-icon-chevron-down' : 'ids-icon-chevron-right'}
          aria-hidden="true"
          style={{ height: '1rem', width: '1rem' }}
        />
        <h3 className="ids-heading-m">{testSuite} ({tests.length})</h3>
      </button>

      <Collapse open={isExpanded}>{children}</Collapse>
    </>
  );
};

/**
 * Displays categorized and filterable test results.
 * Allows viewing request/response/logs and writing justifications per test.
 */
const TestResult = ({ testStatuses, onJustificationsChange, onNext, focusKey, focusNonce, onNavigateToTestCase, isSandbox = false }) => {
  const [filter, setFilter] = useState("all");
  const [justifications, setJustifications] = useState({});
  const [saveAllError, setSaveAllError] = useState(null);

  // Keys (`${testSuite}-${testCase}`) with unsaved justification edits, and a
  // counter that only increments on real user edits. The counter drives the
  // autosave debounce so reloading results from the backend never triggers a save.
  const dirtyKeysRef = useRef(new Set());
  const [editCount, setEditCount] = useState(0);

  // Controlled expansion of individual result steps and refs for scroll-to.
  const [expandedSteps, setExpandedSteps] = useState({});
  const stepRefs = useRef({});

  // Content shown in the request/response/logs dialog ({ title, content }).
  const [dialogContent, setDialogContent] = useState(null);

  // Initialize justifications from backend test results
  useEffect(() => {
    const initialJustifications = {};
    if (testStatuses && Array.isArray(testStatuses)) {
      testStatuses.forEach((test) => {
        if (test.justification) {
          const key = `${test.testSuite}-${test.testCase}`;
          initialJustifications[key] = test.justification;
        }
      });
    }
    setJustifications(initialJustifications);
    // Values loaded from the backend are already persisted — nothing to autosave
    dirtyKeysRef.current.clear();
  }, [testStatuses]);

  // Notify parent when justifications change
  useEffect(() => {
    if (onJustificationsChange) {
      onJustificationsChange(justifications);
    }
  }, [justifications, onJustificationsChange]);

  // React to a focus navigation from a status badge: clear any filter that
  // would hide the target, expand its step, and scroll it into view.
  useEffect(() => {
    if (!focusKey || !focusNonce) return;
    setFilter("all");
    setExpandedSteps((s) => ({ ...s, [focusKey]: true }));
    // Wait for the suite + step expand animations (~300ms) to finish before
    // scrolling, otherwise the target position is measured mid-animation.
    const timer = setTimeout(() => {
      const el = stepRefs.current[focusKey];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 380);
    return () => clearTimeout(timer);
  }, [focusKey, focusNonce]);

  // Tracks per-test save confirmation messages and errors
  const [saveConfirmed, setSaveConfirmed] = useState({});
  const [saveError, setSaveError] = useState({});
  const [savingAll, setSavingAll] = useState(false);

  const hasUnjustifiedFailedTests = (testStatuses || []).some((test) => {
    if (test.status !== 'FAILED') return false;
    const key = `${test.testSuite}-${test.testCase}`;
    const justification = (justifications[key] ?? test.justification ?? '').toString().trim();
    return justification.length === 0;
  });

  const hasNotExecutedTests = (testStatuses || []).some((test) => test.status === 'NOT_EXECUTED');
  const nextButtonDisabled = savingAll || hasUnjustifiedFailedTests || hasNotExecutedTests;
  const nextButtonTooltip = hasNotExecutedTests
    ? 'Alla tester ska köras. Du har ej körda testfall.'
    : hasUnjustifiedFailedTests
      ? 'Alla misslyckade tester ska ha en motivering. Du har misslyckade tester som saknar motivering.'
      : '';

  const handleSaveAllAndNext = async () => {
    if (nextButtonDisabled) return;
    setSavingAll(true);
    try {
      // Save all justifications
      await Promise.all(
        testStatuses.map(async (test) => {
          const key = `${test.testSuite}-${test.testCase}`;
          const justification = justifications[key] || '';
          await saveJustification(test.id, justification);
        })
      );
      dirtyKeysRef.current.clear();
      onNext();
    } catch (err) {
      console.error('Error saving justifications:', err);
      setSaveAllError('Kunde inte spara alla motiveringar. Försök igen.');
    } finally {
      setSavingAll(false);
    }
  };

  // Persist only the justifications the user has actually edited since the
  // last save. Used by the debounced autosave below.
  const autosaveJustifications = useCallback(async () => {
    const keys = Array.from(dirtyKeysRef.current);
    if (keys.length === 0) return;

    const testByKey = new Map(
      (testStatuses || []).map((test) => [`${test.testSuite}-${test.testCase}`, test])
    );

    await Promise.all(
      keys.map(async (key) => {
        const test = testByKey.get(key);
        if (!test || test.id === undefined || test.id === null) {
          // Result is gone or not persisted (e.g. sandbox) — nothing to save
          dirtyKeysRef.current.delete(key);
          return;
        }
        await saveJustification(test.id, justifications[key] || '');
        dirtyKeysRef.current.delete(key);
      })
    );
  }, [testStatuses, justifications]);

  // Autosave justifications 10 s after the last keystroke
  const autosaveStatus = useAutosave(
    autosaveJustifications,
    [editCount],
    { delay: 10000, enabled: editCount > 0 }
  );

  /** Group test results by test suite */
  const categorizedResults = testStatuses.reduce((acc, test) => {
    const testSuite = test.testSuite;
    if (!acc[testSuite]) acc[testSuite] = [];
    acc[testSuite].push(test);
    return acc;
  }, {});

  /** Apply filtering to results */
  const filteredResults = Object.entries(categorizedResults).map(
    ([testSuite, tests]) => {
      const filteredTests = filter === "all"
        ? tests
        : tests.filter((test) => test.status === filter);

      // Sort by testCase name
      const sortedTests = filteredTests.sort((a, b) => 
        a.testCase.localeCompare(b.testCase)
      );

      return { testSuite, tests: sortedTests };
    }
  ).sort((a, b) => a.testSuite.localeCompare(b.testSuite));

  return (
    <div className="ids-mt-4">
      {/* Fixed top-right autosave indicator for justifications */}
      <AutosaveIndicator status={autosaveStatus} fixed />

      <h3 className="ids-heading-s ids-mb-4">Filtrera testresultat</h3>
      
      {/* === Filter Buttons === */}
      <div className="ids-flex ids-mb-6" style={{ gap: '1rem', flexWrap: 'wrap' }}>
        <IDSButton secondary={filter !== "all"} size="m" onClick={() => setFilter("all")}>
          Alla testresultat
        </IDSButton>

        <IDSButton secondary={filter !== "FINISHED"} size="m" onClick={() => setFilter("FINISHED")}>
          Lyckade testresultat
        </IDSButton>

        <IDSButton secondary={filter !== "FAILED"} size="m" onClick={() => setFilter("FAILED")}>
          Misslyckade testresultat
        </IDSButton>

        <IDSButton secondary={filter !== "NOT_EXECUTED"} size="m" onClick={() => setFilter("NOT_EXECUTED")}>
          Ej körda testfall
        </IDSButton>
      </div>

      {/* === Render Filtered Results === */}
      {filteredResults.map(({ testSuite, tests }) => (
        <IDSCard className="ids-mb-6">
          <div key={testSuite}>
            <CollapsibleTestSuite
              testSuite={testSuite}
              tests={tests}
              expandSignal={
                focusKey && tests.some((t) => `${testSuite}-${t.testCase}` === focusKey)
                  ? focusNonce
                  : 0
              }
            >
              <IDSStepper>
                {tests.map((test, index) => {
                 const key = `${testSuite}-${test.testCase}`;
                 const hasJustification = test.status === "FAILED" && !!((justifications[key] || test.justification)?.toString().trim());
                 return (
                   <div
                     key={key}
                     ref={(el) => {
                       if (el) stepRefs.current[key] = el;
                     }}
                   >
                     <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                       <div style={{ flex: 1 }}>
                         <IDSStep
                           headline={test.testCase}
                           srIndicatorText={`Status: ${test.status}`}
                           state={
                             test.status === "FINISHED"
                               ? "valid"
                               : test.status === "NOT_EXECUTED"
                                 ? "warning"
                                 : "invalid"
                           }
                           expanded={!!expandedSteps[key]}
                           onToggleStep={(_stepNumber, next) =>
                             setExpandedSteps((s) => ({ ...s, [key]: next }))
                           }
                         >
                           <div className="ids-mt-2">
                             <p>{test.timestamp ? `${formatTimestamp(test.timestamp)}` : "Tidstämpel saknas"}</p>

                             {test.message && (test.status === "FAILED" || test.status === "NOT_EXECUTED") && (
                               <div
                                 className="ids-mt-3 ids-p-3"
                                 style={{
                                   backgroundColor: test.status === "FAILED" ? '#f6f1f1' : '#fff8e6',
                                   borderLeft: `4px solid ${test.status === "FAILED" ? '#a61d15' : '#d77a00'}`,
                                   borderRadius: '0.25rem',
                                   whiteSpace: 'pre-wrap',
                                   wordBreak: 'break-word',
                                 }}
                               >
                                 <strong>
                                   {test.status === "FAILED" ? 'Testet misslyckades' : 'Testet kördes inte'}
                                 </strong>
                                 <p style={{ margin: '0.5rem 0 0' }}>{test.message}</p>
                               </div>
                             )}

                             {/* Per-step breakdown: which step failed and exactly why */}
                             <TestStepDetails
                               steps={test.steps}
                               testCase={test.testCase}
                               onShowContent={setDialogContent}
                             />

                             {/* Navigate back to this case in the selection step */}
                             {onNavigateToTestCase && (                               <div className="ids-mt-2">
                                 <IDSButton
                                   secondary
                                   size="s"
                                   onClick={() => onNavigateToTestCase(testSuite, test.testCase)}
                                 >
                                   Gå till testfall
                                 </IDSButton>
                               </div>
                             )}

                             {/* Links to open logs/info */}
                             {test.logs && (Array.isArray(test.logs) ? test.logs.length > 0 : test.logs) && (
                               <div className="ids-mt-4">
                                 <IDSButton
                                   secondary
                                   size="s"
                                   onClick={() =>
                                     setDialogContent({
                                       title: `Loggar - ${test.testCase}`,
                                       content: Array.isArray(test.logs) ? test.logs.join("\n") : test.logs,
                                     })
                                   }
                                 >
                                   Se loggar
                                 </IDSButton>
                               </div>
                             )}

                             {/* Justification Input — not applicable in the lab (sandbox) environment,
                                 since nothing is submitted for review there. */}
                             {!isSandbox && test.status === "FAILED" && (
                               <div className="ids-mt-4">
                                 <IDSInput
                                   label="Motivering (obligatorisk)"
                                   type="text"
                                   maxLength={400}
                                   placeholder="* Obligatorisk motivering"
                                   value={justifications[key] || ""}
                                   onChange={(e) => {
                                     const updated = { ...justifications, [key]: e.target.value };
                                     setJustifications(updated);
                                     dirtyKeysRef.current.add(key);
                                     setEditCount((c) => c + 1);
                                   }}
                                 />

                                 <div className="ids-mt-2" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                   <IDSButton
                                     size="s"
                                     onClick={async () => {
                                       try {
                                         await saveJustification(test.id, justifications[key] || '');
                                         dirtyKeysRef.current.delete(key);
                                         setSaveConfirmed((s) => ({ ...s, [key]: true }));
                                         setSaveError((s) => ({ ...s, [key]: false }));
                                         setTimeout(() => setSaveConfirmed((s) => ({ ...s, [key]: false })), 3000);
                                       } catch (err) {
                                         console.error('Error saving justification', err);
                                         setSaveError((s) => ({ ...s, [key]: true }));
                                         setTimeout(() => setSaveError((s) => ({ ...s, [key]: false })), 5000);
                                       }
                                     }}
                                   >
                                     Spara
                                   </IDSButton>

                                   {autosaveStatus === 'idle' && saveConfirmed[key] && (
                                     <IDSAlert
                                       type="success"
                                       compact
                                       style={{ padding: "0.3125rem 0.9375rem", fontSize: "0.875rem", lineHeight: "1rem", margin: 0 }}
                                     >
                                       <span style={{ margin: 0 }}>Motivering sparad</span>
                                     </IDSAlert>
                                   )}
                                   {saveError[key] && (
                                     <IDSAlert
                                       type="error"
                                       compact
                                       style={{ padding: "0.3125rem 0.9375rem", fontSize: "0.875rem", lineHeight: "1rem", margin: 0 }}
                                     >
                                       <span style={{ margin: 0 }}>Kunde inte spara motivering</span>
                                     </IDSAlert>
                                   )}
                                 </div>
                               </div>
                             )}
                           </div>
                         </IDSStep>
                       </div>

                       {!isSandbox && hasJustification && (
                         <IDSTooltip
                           position="top-right"
                           trigger={
                             <span
                               aria-label="Klar med motivering"
                               tabIndex={0}
                               style={{
                                 display: 'inline-flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 width: '1.25rem',
                                 height: '1.25rem',
                                 borderRadius: '999px',
                                 backgroundColor: '#dff6e8',
                                 color: '#1f7a45',
                                 fontSize: '0.8rem',
                                 fontWeight: 700,
                                 lineHeight: 1,
                                 cursor: 'pointer',
                               }}
                             >
                               ✓
                             </span>
                           }
                         >
                           <div style={{ maxWidth: '16rem', whiteSpace: 'normal' }}>Klar med motivering</div>
                         </IDSTooltip>
                       )}
                     </div>
                   </div>
                 );
                })}
              </IDSStepper>
            </CollapsibleTestSuite>
          </div>
        </IDSCard>  
      ))}

      {/* Next Step Button */}
      {onNext && (
        <div className="ids-mt-6">
          {saveAllError && (
            <div className="ids-mb-2">
              <IDSAlert type="error"><p>{saveAllError}</p></IDSAlert>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <IDSButton size="m" onClick={handleSaveAllAndNext} disabled={nextButtonDisabled}>
              {savingAll ? "Sparar..." : "Gå vidare till frågor"}
            </IDSButton>
            {nextButtonTooltip && <InfoTooltip info={nextButtonTooltip} />}
          </div>
        </div>
      )}

      {/* Dialog for viewing full request / response / logs */}
      <IDSDialog
        show={!!dialogContent}
        headline={dialogContent?.title}
        dismissible
        maxWidth="800px"
        onVisibilityChange={(visible) => {
          if (!visible) setDialogContent(null);
        }}
      >
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
          {dialogContent?.content}
        </pre>
      </IDSDialog>
    </div>
  );
};

export default TestResult;
